import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { WidgetError } from "@/components/WidgetError";
import { PaginationBar } from "@/components/PaginationBar";
import { fmtMoney, randToCents } from "@/lib/format";
import { toast } from "sonner";
import { Edit3, CheckCircle2, XCircle, FileText } from "lucide-react";

const STATUS_STYLES = {
  pending:      "bg-amber-50 text-amber-800 border-amber-300",
  needs_review: "bg-red-50 text-red-800 border-red-300",
  approved:     "bg-blue-50 text-blue-800 border-blue-300",
  invoiced:     "bg-green-50 text-green-800 border-green-300",
  skipped:      "bg-slate-100 text-slate-700 border-slate-300",
};

export default function UninvoicedChargesPage() {
  const [data, setData] = useState({ items: [], counters: {} });
  const [statusFilter, setStatusFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [batching, setBatching] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, c] = await Promise.all([
        api.get("/finance/uninvoiced-charges", { params: {
          page, limit: 25,
          ...(statusFilter && { status: statusFilter }),
          ...(customerFilter && { customer_account_id: customerFilter }),
        }}),
        api.get("/customers", { params: { limit: 100 } }),
      ]);
      setData(d.data);
      setCustomers(c.data.items || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [page, statusFilter, customerFilter]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    const invoiceable = data.items.filter((c) =>
      c.status !== "invoiced" && c.status !== "skipped" && c.unit_price_cents != null,
    );
    if (selected.size === invoiceable.length) setSelected(new Set());
    else setSelected(new Set(invoiceable.map((c) => c.id)));
  };

  const custName = (id) => customers.find((c) => c.id === id)?.display_name || id?.slice(0, 8);

  const approve = async (id) => {
    try { await api.post(`/finance/uninvoiced-charges/${id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatError(e, "Approve failed")); }
  };
  const skip = async (id) => {
    const reason = window.prompt("Skip reason (required, min 3 chars):");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/finance/uninvoiced-charges/${id}/skip`, { reason });
      toast.success("Skipped"); load();
    } catch (e) { toast.error(formatError(e, "Skip failed")); }
  };
  const batchInvoice = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Create invoices from ${selected.size} selected charges? Grouped by customer.`)) return;
    setBatching(true);
    try {
      const r = await api.post("/finance/uninvoiced-charges/batch-invoice",
        { charge_ids: Array.from(selected) });
      toast.success(
        `Created ${r.data.customers_invoiced} invoice(s) from ${r.data.charges_invoiced} charges`,
      );
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatError(e, "Batch failed")); }
    finally { setBatching(false); }
  };

  if (error) return <WidgetError error={error} onRetry={load}
                                 title="Could not load uninvoiced charges" testId="uic-error"/>;

  const counters = data.counters || {};

  return (
    <div data-testid="uninvoiced-charges-page">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-3xl font-black tracking-tight">Uninvoiced Charges</h1>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Completed customer orders create uninvoiced charges automatically. Review, edit, approve or skip,
        then batch-invoice by customer.
      </p>

      <div className="flex gap-2 items-end mb-4 flex-wrap" data-testid="uic-filters">
        <Counter label="Pending" value={counters.pending} color="amber"
                 onClick={() => { setStatusFilter("pending"); setPage(1); }} />
        <Counter label="Needs review" value={counters.needs_review} color="red"
                 onClick={() => { setStatusFilter("needs_review"); setPage(1); }} />
        <Counter label="Approved" value={counters.approved} color="blue"
                 onClick={() => { setStatusFilter("approved"); setPage(1); }} />
        <Counter label="Invoiced" value={counters.invoiced} color="green"
                 onClick={() => { setStatusFilter("invoiced"); setPage(1); }} />
        <Counter label="Skipped" value={counters.skipped} color="slate"
                 onClick={() => { setStatusFilter("skipped"); setPage(1); }} />

        <div className="ml-auto flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  data-testid="uic-status-filter"
                  className="h-9 border border-slate-300 rounded px-2 text-sm font-mono">
            <option value="">All statuses</option>
            <option value="pending">pending</option>
            <option value="needs_review">needs_review</option>
            <option value="approved">approved</option>
            <option value="invoiced">invoiced</option>
            <option value="skipped">skipped</option>
          </select>
          <select value={customerFilter} onChange={(e) => { setCustomerFilter(e.target.value); setPage(1); }}
                  data-testid="uic-customer-filter"
                  className="h-9 border border-slate-300 rounded px-2 text-sm max-w-[220px]">
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
          </select>
        </div>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <Button onClick={batchInvoice} disabled={selected.size === 0 || batching}
                data-testid="uic-batch-invoice-btn"
                className="rounded bg-blue-700 hover:bg-blue-800">
          <FileText size={14} className="mr-2"/>
          {batching ? "Creating…" :
                     `Batch invoice ${selected.size ? `(${selected.size})` : ""}`}
        </Button>
        <span className="text-xs text-slate-500 font-mono ml-auto">Total: {data.total || 0}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="tbl w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 label-overline text-slate-600">
            <tr>
              <th className="w-8 px-3 py-2">
                <input type="checkbox" onChange={toggleAll}
                       data-testid="uic-select-all"
                       checked={selected.size > 0 &&
                         selected.size === data.items.filter((c) =>
                           c.status !== "invoiced" && c.status !== "skipped" &&
                           c.unit_price_cents != null,
                         ).length}/>
              </th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Service</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Rate</th>
              <th className="text-right px-3 py-2">Line</th>
              <th className="text-left px-3 py-2">Rate source</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="w-40 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-8 text-center text-slate-500">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-xs text-slate-500">
                No charges match this filter.
              </td></tr>
            ) : data.items.map((c) => {
              const sel = selected.has(c.id);
              const invoiceable = c.status !== "invoiced" && c.status !== "skipped" && c.unit_price_cents != null;
              return (
                <tr key={c.id} className={"border-b border-slate-100 " + (sel ? "bg-blue-50" : "")}
                    data-testid={`uic-row-${c.id}`}>
                  <td className="px-3 py-2">
                    <input type="checkbox" disabled={!invoiceable} checked={sel}
                           onChange={() => toggle(c.id)}
                           data-testid={`uic-select-${c.id}`}/>
                  </td>
                  <td className="px-3 py-2">{custName(c.customer_account_id)}</td>
                  <td className="px-3 py-2">
                    {c.service_type_snapshot?.label || "—"}
                    <div className="text-[10px] text-slate-400 font-mono">
                      order {c.order_id?.slice(0, 8)}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-right">{c.quantity}</td>
                  <td className="px-3 py-2 font-mono text-right">
                    {c.unit_price_cents == null ? "—" : fmtMoney(c.unit_price_cents)}
                  </td>
                  <td className="px-3 py-2 font-mono text-right font-bold">
                    {c.line_total_cents == null ? "—" : fmtMoney(c.line_total_cents)}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] font-mono uppercase text-slate-500">
                      {c.rate_source}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={"px-2 py-0.5 rounded text-[10px] font-bold border " +
                      (STATUS_STYLES[c.status] || "bg-slate-50 text-slate-600")}
                          data-testid={`uic-status-${c.id}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 flex gap-1 justify-end">
                    {(c.status === "pending" || c.status === "needs_review") && (
                      <>
                        <button onClick={() => setEditingCharge(c)}
                                data-testid={`uic-edit-${c.id}`}
                                title="Edit"
                                className="p-1 rounded hover:bg-slate-100 text-slate-600">
                          <Edit3 size={14}/>
                        </button>
                        <button onClick={() => approve(c.id)}
                                data-testid={`uic-approve-${c.id}`}
                                title="Approve"
                                disabled={c.unit_price_cents == null}
                                className="p-1 rounded hover:bg-green-50 text-green-700 disabled:opacity-40">
                          <CheckCircle2 size={14}/>
                        </button>
                        <button onClick={() => skip(c.id)}
                                data-testid={`uic-skip-${c.id}`}
                                title="Skip"
                                className="p-1 rounded hover:bg-red-50 text-red-600">
                          <XCircle size={14}/>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} pages={data.pages || 1} onChange={setPage}/>

      <ChargeEditor charge={editingCharge}
                    onClose={() => setEditingCharge(null)}
                    onSaved={() => { setEditingCharge(null); load(); }}/>
    </div>
  );
}

function Counter({ label, value, color, onClick }) {
  const tones = {
    amber: "border-amber-300 text-amber-800 bg-amber-50",
    red: "border-red-300 text-red-800 bg-red-50",
    blue: "border-blue-300 text-blue-800 bg-blue-50",
    green: "border-green-300 text-green-800 bg-green-50",
    slate: "border-slate-300 text-slate-700 bg-slate-50",
  };
  return (
    <button onClick={onClick}
            data-testid={`uic-counter-${label.toLowerCase().replace(" ", "-")}`}
            className={"border rounded px-3 py-1 text-left hover:brightness-95 " + tones[color]}>
      <div className="label-overline text-[10px]">{label}</div>
      <div className="font-mono font-bold text-lg leading-none">{value || 0}</div>
    </button>
  );
}

function ChargeEditor({ charge, onClose, onSaved }) {
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (charge) {
      setQty(String(charge.quantity ?? ""));
      setPrice(charge.unit_price_cents != null ? (charge.unit_price_cents / 100).toString() : "");
      setNotes(charge.notes || "");
    }
  }, [charge]);

  if (!charge) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (qty !== "") payload.quantity = parseFloat(qty);
      if (price !== "") payload.unit_price_cents = randToCents(price);
      if (notes) payload.notes = notes;
      await api.patch(`/finance/uninvoiced-charges/${charge.id}`, payload);
      toast.success("Charge updated");
      onSaved();
    } catch (e) { toast.error(formatError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!charge} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="uic-editor-dialog">
        <DialogHeader>
          <DialogTitle>Edit charge</DialogTitle>
          <DialogDescription>
            {charge.service_type_snapshot?.label} · order {charge.order_id?.slice(0, 8)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-overline">Quantity</Label>
              <Input type="number" step="0.5" value={qty} onChange={(e) => setQty(e.target.value)}
                     data-testid="uic-editor-qty"
                     className="rounded h-10 mt-1 font-mono"/>
            </div>
            <div>
              <Label className="label-overline">Unit price (R)</Label>
              <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
                     data-testid="uic-editor-price"
                     className="rounded h-10 mt-1 font-mono"/>
            </div>
          </div>
          <div>
            <Label className="label-overline">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                   data-testid="uic-editor-notes"
                   className="rounded h-10 mt-1"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="uic-editor-save"
                  className="rounded bg-slate-900 hover:bg-slate-800">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
